/**
 * The frozen strip's words (SA-461, sweep spec §7.1, D10): the title, the six
 * facts and the "now" line, projected from the order, the process read and
 * the quote versions — pure, `now` injectable, so the strip is markup.
 * Core facts only: the title reads the intake pair, never the module's
 * sectors (ruling R1); the owner (SA-1035) is the server's name with its
 * basis, the assignee a name from the page's map, each else a muted word,
 * never an id (SA-330).
 * A running record without a current_step (null) is still running, never closed.
 * The due time is `dueLine`'s, with the deployment zone once (SA-1077).
 */
import { recordQuote } from './quoteText'
import { dayText, dueLine, lowerWords, outcomeWords } from '@/components/process/stepLiveText'
import type { Order, OrderOwner, QuoteVersion } from '@/types/commerce'
import type { RecordProcessRead } from '@/types/process'
import { formatCurrency } from '@/utils/currency'
import { INTAKE_SLOT, intakePair, intakeSlot, intakeValue } from './intakeSnapshot'
import { placeCodes } from './place'
import { NO_OWNER, OWNER_BASIS_WORDS, UNKNOWN_PERSON } from './ownerText'

export interface StripFact {
  label: string
  value: string
  /** A muted regular-weight tail after the value (the customer type). */
  note?: string
  /** The value itself is muted (Unassigned). */
  muted?: boolean
  /** The full text when the value clamps to one line. */
  title?: string
}

type StripOrder = Pick<Order, 'customDetails' | 'customerName' | 'assignedTo' | 'owner'>

export const joinParts = (parts: Array<string | null | undefined | false>): string => parts.filter(Boolean).join(' · ')

export function stripTitle(order: Pick<Order, 'customDetails'>): string | null {
  const pair = intakePair(order)
  if (pair) return joinParts([pair, intakeSlot(order, INTAKE_SLOT.cargoSize)])
  return intakeSlot(order, INTAKE_SLOT.cargoDescription)
}

/** The title's parts (SA-1031): the strip renders the pair as stations and
 * the rest as text; `stripTitle` keeps the one-string form. SA-1051: the
 * pair's codes ride along, so the strip's one lookup asks for every code of
 * every place (a choice holds more than one). */
export function stripTitleParts(order: Pick<Order, 'customDetails'>): { origin: string | null; destination: string | null; originCodes: string[]; destinationCodes: string[]; cargoSize: string | null; fallback: string | null } {
  const origin = intakeSlot(order, INTAKE_SLOT.origin)
  const destination = intakeSlot(order, INTAKE_SLOT.destination)
  if (origin && destination) {
    return {
      origin, destination,
      originCodes: placeCodes(intakeValue(order, INTAKE_SLOT.origin)), destinationCodes: placeCodes(intakeValue(order, INTAKE_SLOT.destination)),
      cargoSize: intakeSlot(order, INTAKE_SLOT.cargoSize), fallback: null,
    }
  }
  return { origin: null, destination: null, originCodes: [], destinationCodes: [], cargoSize: null, fallback: intakeSlot(order, INTAKE_SLOT.cargoDescription) }
}

/** `undefined` while the versions load: the fact is left out, not "No quote". */
export function quoteWords(versions: QuoteVersion[] | undefined): string | null {
  if (versions === undefined) return null
  const latest = recordQuote(versions)
  if (!latest) return 'No quote'
  return `v${latest.versionNumber} ${formatCurrency(latest.amountCents, latest.currency)} · ${latest.expired ? 'expired' : latest.status}`
}

/** SA-1035: the record's owner, worded with how they came to own it. */
function ownerFact(owner: OrderOwner | null | undefined): StripFact {
  if (!owner) return { label: 'Owner', value: NO_OWNER, muted: true }
  const note = OWNER_BASIS_WORDS[owner.basis]
  return owner.name
    ? { label: 'Owner', value: owner.name, note }
    : { label: 'Owner', value: UNKNOWN_PERSON, muted: true, note }
}

export function stripFacts(
  order: StripOrder,
  { assigneeNames = {}, versions }: { assigneeNames?: Record<string, string>; versions?: QuoteVersion[] } = {},
): StripFact[] {
  const facts: StripFact[] = []
  const company = intakeSlot(order, INTAKE_SLOT.company) ?? order.customerName
  const type = intakeSlot(order, INTAKE_SLOT.customerType)
  if (company) facts.push({ label: 'Customer', value: company, ...(type ? { note: lowerWords(type) } : {}) })
  const cargo = joinParts([intakePair(order), intakeSlot(order, INTAKE_SLOT.cargoSize)])
  if (cargo) facts.push({ label: 'Cargo', value: cargo })
  const when = intakeSlot(order, INTAKE_SLOT.neededWindow)
  if (when) facts.push({ label: 'When', value: when, title: when })
  const deadline = intakeSlot(order, INTAKE_SLOT.quoteDeadline)
  if (deadline) facts.push({ label: 'Quote by', value: deadline })
  facts.push(ownerFact(order.owner))
  const assignee = order.assignedTo ? assigneeNames[order.assignedTo] : null
  facts.push(assignee
    ? { label: 'Assigned to', value: assignee }
    : { label: 'Assigned to', value: order.assignedTo ? 'Assigned' : 'Unassigned', muted: true })
  const quote = quoteWords(versions)
  if (quote) facts.push({ label: 'Quote', value: quote })
  return facts
}

export type StripNow =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'running'; step: string | null; seat: string | null; due: string | null; overdue: boolean }
  | { kind: 'closed'; at: string | null; outcome: string | null }

export function stripNow(record: RecordProcessRead | null | undefined, now: Date = new Date(), zone?: string): StripNow {
  if (record === undefined) return { kind: 'loading' }
  if (record === null) return { kind: 'none' }
  if (record.status !== 'running') {
    return { kind: 'closed', at: dayText(record.closed_at, now), outcome: outcomeWords(record.closed_outcome) }
  }
  const step = record.current_step
  if (step) {
    return { kind: 'running', step: `${step.number} ${step.title}`, seat: step.seat_label, due: step.due_at ? dueLine(step.due_at, now, zone) : null, overdue: step.overdue }
  }
  return { kind: 'running', step: null, seat: null, due: null, overdue: false }
}
